import { Fragment, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from '@xstate5/react';

import { AIBox } from '@crazyegginc/hatch';

import { useCrazyAI, useUserLanguage, useSelectedSite, useAuthContext } from '/src/hooks';
import { SidebarCollapsiblePanel, SidebarPanelContent, SidebarPanelHeader } from '../SidebarPanel';

import { ReactComponent as SparklesIcon } from '@crazyegginc/hatch/dist/images/icon-sparkles-filled.svg';
import classNames from 'classnames';

export function AiPanel({ actorRef }) {
  const location = useLocation();
  const userPreferredLanguage = useUserLanguage();
  const { selectedSite } = useSelectedSite();
  const { currentUser } = useAuthContext();
  const [opened, setOpened] = useState(true);

  const toggleOpened = () => setOpened((o) => !o);

  const rawEvents = useSelector(actorRef, (state) => state.context.rawEvents);

  const enabled = !!selectedSite?.name && !!rawEvents && Array.isArray(rawEvents) && rawEvents.length > 0;

  const seek = useCallback(
    (ticks) => {
      if (Number.isFinite(ticks)) {
        actorRef.send({ type: 'SEEK', ticks: ticks * 1000 });
      }
    },
    [actorRef],
  );

  const params = useMemo(
    () => ({
      data: JSON.stringify(rawEvents),
      language: userPreferredLanguage,
      site: selectedSite?.name,
    }),
    [userPreferredLanguage, selectedSite?.name, rawEvents],
  );

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
      userId: currentUser?.userId,
    }),
    [location.pathname, location.search, currentUser?.userId],
  );

  const {
    data,
    error: aiError,
    refetch,
    positiveFeedback,
    negativeFeedback,
  } = useCrazyAI({
    type: 'recording-events-summary',
    params,
    context,
    enabled: enabled && !!params.site,
  });

  const { insights, error } = useMemo(() => {
    let insights;
    try {
      if (data?.ready) {
        insights = JSON.parse(data?.text).insights;
        if (insights.length < 1) {
          return { error: true };
        } else {
          return { insights };
        }
      }
    } catch {
      return { error: true };
    }
    return {};
  }, [data]);

  if (!enabled) return null;

  return (
    <SidebarCollapsiblePanel>
      <SidebarPanelHeader className="h-[55px]" opened={opened} toggleOpened={toggleOpened}>
        <div
          className="mr-3.5 flex h-[35px] w-[35px] items-center justify-center rounded-full bg-[#3C424D]"
          role="presentation"
        >
          <SparklesIcon className="-ml-px text-lavender-500 fill-current w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1 flex items-center">
          <button onClick={toggleOpened} type="button" className="truncate text-left font-semibold text-white">
            AI analysis
          </button>
        </div>
      </SidebarPanelHeader>
      <SidebarPanelContent opened={opened}>
        <div className="bg-charade-500 max-h-[166px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-woodsmoke-500 [&>*]:p-5">
          <AIBox
            size="lg"
            id="recording-events-summary"
            error={error || aiError}
            loading={!selectedSite?.name || !data?.ready}
            selectedFeedback={data?.feedback}
            refetch={refetch}
            onPositive={positiveFeedback}
            onNegative={negativeFeedback}
            showChangeLanguage={true}
            border={false}
            darkMode={true}
            fill={false}
            showIcon={false}
            className="!leading-normal"
          >
            {insights?.map((i, idx) => {
              const hasAction = i.action && i.action.length > 0;

              return (
                <div key={`recording-events-summary-${idx}`}>
                  {i.header ? <div className="text-body-1 text-white font-bold">{i.header}</div> : null}
                  <TimestampedParagraph
                    text={i.detail}
                    onTimestampClick={(timestamp) => seek(timestamp)}
                    className={classNames('text-body-2 text-white', hasAction ? 'mb-2' : 'mb-4')}
                  />
                  {hasAction ? (
                    <TimestampedParagraph
                      text={i.action}
                      onTimestampClick={(timestamp) => seek(timestamp)}
                      className="mb-4 text-body-2 text-white"
                    />
                  ) : null}
                </div>
              );
            })}
          </AIBox>
        </div>
      </SidebarPanelContent>
    </SidebarCollapsiblePanel>
  );
}

function TimestampedParagraph({ text, onTimestampClick, className }) {
  const timestampRegex = /\b\d{1,2}:\d{2}(?::\d{2})?\b/g;
  const parts = text.split(new RegExp(`(${timestampRegex.source})`, 'g'));

  return (
    <p className={className}>
      {parts.map((part, index) => {
        if (timestampRegex.test(part)) {
          return (
            <button key={index} onClick={() => onTimestampClick(timestampToSeconds(part))} className="text-link">
              {part}
            </button>
          );
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </p>
  );
}

function timestampToSeconds(timestamp) {
  if (typeof timestamp !== 'string') return 0;

  const parts = timestamp.split(':').map(Number);

  if (parts.some(isNaN)) return 0;

  if (parts.length === 3) {
    // hh:mm:ss
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  } else if (parts.length === 2) {
    // mm:ss
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  } else if (parts.length === 1) {
    // Just seconds
    return parts[0];
  }

  return 0;
}
